import React from 'react';
import { Image, ImageProps, StyleProp, ViewStyle, ImageStyle } from 'react-native';
import { Box, Text } from '@/components/ui/primitives';
import { config } from '@/config/env';
import { BoxProps, TextProps } from '@/lib/styling';
import { Theme } from '@/theme';
import { StyleSheet } from 'react-native-unistyles';

const styles = StyleSheet.create(() => ({
  image: {
    height: '100%',
    width: '100%',
  },
}));

interface AvatarProps extends BoxProps {
  size?: number;
  children?: React.ReactNode;
  className?: string; // Keep for compatibility
  style?: StyleProp<ViewStyle>;
}

interface AvatarImageProps extends Omit<ImageProps, 'source' | 'src'> {
  src?: string | null | undefined;
  style?: StyleProp<ImageStyle>;
}

interface AvatarFallbackProps extends BoxProps {
  children?: React.ReactNode;
  textProps?: TextProps;
  className?: string; // Compatibility
  style?: StyleProp<ViewStyle>;
}

interface GradientAvatarFallbackProps extends BoxProps {
  name?: string;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

const gradientColors: (keyof Theme['colors'])[] = [
  'primary',
  'secondary',
  'destructive',
  'success',
  'primary', // warning replacement
  'secondary',
  'primary',
];

function getGradientColorForLeague(name: string): keyof Theme['colors'] {
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradientColors[hash % gradientColors.length] ?? 'primary';
}

export function Avatar({ size = 40, style, children, ...props }: AvatarProps) {
  return (
    <Box
      overflow="hidden"
      borderRadius="full"
      borderWidth={1}
      borderColor="border"
      width={size}
      height={size}
      style={style}
      {...props}
    >
      {children}
    </Box>
  );
}

export function resolveAvatarUri(src: string): string {
  if (!src) {
    return src;
  }
  if (/^https?:\/\//i.test(src) || src.startsWith('data:') || src.startsWith('file:') || src.startsWith('asset:')) {
    return src;
  }
  const baseUrl = config.apiUrl?.replace(/\/$/, '') ?? '';
  if (src.startsWith('/')) {
    return `${baseUrl}${src}`;
  }
  return `${baseUrl}/${src}`;
}

export function AvatarImage({ src, style, ...props }: AvatarImageProps) {
  if (!src) return null;
  const resolved = resolveAvatarUri(src);

  return (
    <Image
      source={{ uri: resolved }}
      style={[styles.image, style]}
      resizeMode="cover"
      {...props}
    />
  );
}

export function AvatarFallback({ style, children, textProps, ...props }: AvatarFallbackProps) {
  return (
    <Box
      width="100%"
      height="100%"
      alignItems="center"
      justifyContent="center"
      backgroundColor="paperDark"
      style={style}
      {...props}
    >
      <Text
        fontWeight="700"
        color="text"
        variant="caption"
        {...textProps}
      >
        {children}
      </Text>
    </Box>
  );
}

export function GradientAvatarFallback({
  name,
  style,
  children,
  ...props
}: GradientAvatarFallbackProps) {
  const colorKey = getGradientColorForLeague(name || 'Default');

  return (
    <Box
      width="100%"
      height="100%"
      alignItems="center"
      justifyContent="center"
      borderWidth={0}
      borderColor="transparent"
      backgroundColor={colorKey}
      style={style}
      {...props}
    >
      <Text
        variant="h3"
        color="primaryForeground"
      >
        {children}
      </Text>
    </Box>
  );
}
